import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { fmtPrice, fmtKm, NoPhoto } from "../components/VehicleCard";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { ArrowLeft, MessageCircle, Phone, Calendar, Gauge, Fuel, Cog, Palette, Zap, Gauge as Disp, DoorOpen, ListChecks, Facebook, Share2, ChevronLeft, ChevronRight } from "lucide-react";

const PHONE_RAW = "351924020938";

export default function VehicleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setVehicle(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="App">
        <Navbar />
        <div className="pt-40 pb-40 text-center">
          <h1 className="font-heading font-bold text-3xl">Viatura não encontrada</h1>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-primary text-primary-foreground">Voltar</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return <div className="App"><Navbar /><div className="pt-40 flex justify-center"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;
  }

  const hasImages = vehicle.images && vehicle.images.length > 0;
  const images = hasImages ? vehicle.images.map(fileUrl) : [];
  const hasPromo = vehicle.promo_price != null && vehicle.promo_price > 0 && vehicle.promo_price < vehicle.price;
  const waMsg = encodeURIComponent(`Olá! Tenho interesse na viatura ${vehicle.brand} ${vehicle.model} (${vehicle.year}).`);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${vehicle.brand} ${vehicle.model} (${vehicle.year}) — STAND LF`;
  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
  const waShare = `https://wa.me/?text=${encodeURIComponent(shareText + " " + pageUrl)}`;

  const specs = [
    { icon: Calendar, label: "Ano", value: vehicle.month ? `${String(vehicle.month).padStart(2, "0")}/${vehicle.year}` : vehicle.year },
    { icon: Gauge, label: "Quilómetros", value: fmtKm(vehicle.mileage) },
    { icon: Fuel, label: "Combustível", value: vehicle.fuel },
    { icon: Cog, label: "Caixa", value: vehicle.gearbox },
    vehicle.color && { icon: Palette, label: "Cor", value: vehicle.color },
    vehicle.horsepower && { icon: Zap, label: "Potência", value: `${vehicle.horsepower} cv` },
    vehicle.displacement && { icon: Disp, label: "Cilindrada", value: `${vehicle.displacement} cc` },
    vehicle.doors && { icon: DoorOpen, label: "Portas", value: vehicle.doors },
  ].filter(Boolean);

  const displayPrice = hasPromo ? vehicle.promo_price : vehicle.price;
  const seoTitle = `${vehicle.brand} ${vehicle.model} (${vehicle.year}) — STAND LF`;
  const seoDesc = (vehicle.description && vehicle.description.slice(0, 155)) ||
    `${vehicle.brand} ${vehicle.model} de ${vehicle.year}, ${fmtKm(vehicle.mileage)}, ${vehicle.fuel}. Viatura usada com garantia na STAND LF, São João de Ovar.`;
  const vehicleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Car",
    name: `${vehicle.brand} ${vehicle.model}`,
    brand: { "@type": "Brand", name: vehicle.brand },
    model: vehicle.model,
    vehicleModelDate: String(vehicle.year),
    ...(hasImages ? { image: images[0] } : {}),
    ...(vehicle.description ? { description: vehicle.description } : {}),
    mileageFromOdometer: { "@type": "QuantitativeValue", value: vehicle.mileage, unitCode: "KMT" },
    fuelType: vehicle.fuel,
    vehicleTransmission: vehicle.gearbox,
    offers: {
      "@type": "Offer",
      price: displayPrice,
      priceCurrency: "EUR",
      availability: vehicle.sold ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      url: pageUrl,
      seller: { "@type": "AutoDealer", name: "STAND LF" },
    },
  };

  return (
    <div className="App">
      <Seo title={seoTitle} description={seoDesc} canonical={pageUrl.split("?")[0]} jsonLd={vehicleJsonLd} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-[96px]">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Voltar ao catálogo
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* GALLERY */}
          <div className="lg:col-span-3">
            <div className="relative rounded-3xl overflow-hidden bg-muted aspect-[4/3] group">
              {hasImages ? (
                <img data-testid="gallery-main" src={images[active]} alt={vehicle.model} className="w-full h-full object-cover" />
              ) : (
                <NoPhoto className="w-full h-full" />
              )}
              {images.length > 1 && (
                <>
                  <button
                    data-testid="gallery-prev"
                    onClick={() => setActive((active - 1 + images.length) % images.length)}
                    aria-label="Foto anterior"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-foreground shadow-lg flex items-center justify-center transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    data-testid="gallery-next"
                    onClick={() => setActive((active + 1) % images.length)}
                    aria-label="Foto seguinte"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-foreground shadow-lg flex items-center justify-center transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/55 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {active + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button key={img} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)} className={`shrink-0 w-24 h-20 rounded-xl overflow-hidden border-2 transition-colors ${active === i ? "border-primary" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-secondary/60 rounded-3xl p-7 border border-black/5">
              <span className="text-primary font-bold text-xs tracking-[0.2em] uppercase">{vehicle.brand}</span>
              <h1 className="font-heading font-black text-3xl tracking-tight mt-1">{vehicle.model}</h1>
              {vehicle.sold ? (
                <div className="mt-5">
                  <span data-testid="detail-price" className="inline-block bg-zinc-900 text-white text-sm font-bold px-4 py-2 rounded-full uppercase tracking-wide">Vendido</span>
                </div>
              ) : hasPromo ? (
                <div className="mt-5">
                  <span className="inline-block bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-2">Promoção</span>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <p className="font-heading font-bold text-4xl text-primary" data-testid="detail-price">{fmtPrice(vehicle.promo_price)}</p>
                    <p className="text-xl text-muted-foreground line-through">{fmtPrice(vehicle.price)}</p>
                  </div>
                </div>
              ) : (
                <p className="font-heading font-bold text-4xl text-primary mt-5" data-testid="detail-price">{fmtPrice(vehicle.price)}</p>
              )}
              <div className="flex flex-col gap-3 mt-7">
                <a data-testid="detail-whatsapp" href={`https://wa.me/${PHONE_RAW}?text=${waMsg}`} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-transform hover:-translate-y-0.5">
                    <MessageCircle className="w-4 h-4 mr-2" /> Contactar por WhatsApp
                  </Button>
                </a>
                <a data-testid="detail-phone" href={`tel:${PHONE_RAW}`}>
                  <Button variant="outline" className="w-full h-12 rounded-full font-semibold border-2">
                    <Phone className="w-4 h-4 mr-2" /> Ligar agora
                  </Button>
                </a>
              </div>
              <div className="mt-6 pt-5 border-t border-black/5">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3 flex items-center gap-1.5">
                  <Share2 className="w-3.5 h-3.5" /> Partilhar
                </p>
                <div className="flex gap-2">
                  <a data-testid="share-facebook" href={fbShare} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full h-11 rounded-full font-semibold border-2 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors">
                      <Facebook className="w-4 h-4 mr-2" /> Facebook
                    </Button>
                  </a>
                  <a data-testid="share-whatsapp" href={waShare} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full h-11 rounded-full font-semibold border-2 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors">
                      <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BENTO SPECS */}
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-16 mb-6">Especificações</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {specs.map((s, i) => (
            <div key={s.label} data-testid={`spec-${i}`} className="bg-white rounded-2xl p-6 border border-black/5">
              <s.icon className="w-6 h-6 text-primary mb-3" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</p>
              <p className="font-heading font-bold text-lg mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {vehicle.extras && (
          <div className="bg-accent/50 rounded-3xl p-7 border border-black/5 mt-6">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><ListChecks className="w-5 h-5 text-primary" /> Extras</h3>
            <p className="text-muted-foreground whitespace-pre-line leading-relaxed">{vehicle.extras}</p>
          </div>
        )}

        {vehicle.description && (
          <div className="mt-6">
            <h3 className="font-heading font-bold text-lg mb-3">Descrição</h3>
            <p className="text-muted-foreground whitespace-pre-line leading-relaxed">{vehicle.description}</p>
          </div>
        )}
      </div>

      <div className="h-24" />
      <Footer />
    </div>
  );
}
